import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MenuIcon from '@material-ui/icons/Menu';
import Drawer from '@material-ui/core/Drawer';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import NavbarListItem from './NavbarListItem';
import { GET_TOKEN, DELETE_REFRESH_TOKEN } from '../../gql/auth';
import logo from '../../assets/logo.png';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  title: {
    flexGrow: 1,
    letterSpacing: '1px',
  },
  accountMenuList: {
    marginRight: theme.spacing(2),
  },
  offset: theme.mixins.toolbar,
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: 'theme.transitions.duration.enteringScreen',
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  drawerOpen: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerClose: {
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  name: {
    borderLeft: '1px solid white',
    paddingLeft: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  logo: {
    width: 'auto',
    maxWidth: '100%',
    maxHeight: '64px',
  },
}));

const Navbar = (props) => {
  const { window } = props;
  const history = useHistory();
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { data } = useQuery(GET_TOKEN);
  const [logout] = useMutation(DELETE_REFRESH_TOKEN, {
    update: (cache) => {
      cache.writeQuery({
        query: GET_TOKEN,
        data: {
          token: '',
          isLoggedIn: false,
          netId: '',
          loading: true,
          role: '',
        },
      });
    },
    // refetchQueries: ['GET_TOKEN'],
  });
  const matches = useMediaQuery('(min-width:600px)');
  const { role } = data;

  const handleLogout = () => {
    logout();
    history.push('/logout');
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setMobileOpen(false);
  };

  // eslint-disable-next-line no-shadow
  const handleMeunIconClick = (matches) => {
    if (matches) {
      setDrawerOpen(true);
    } else {
      setMobileOpen(!mobileOpen);
    }
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const drawer = (
    <>
      <div className={classes.toolbarIcon}>
        <IconButton onClick={handleDrawerClose}>
          <ArrowBackIosIcon />
        </IconButton>
      </div>
      <Divider />
      <List>
        <NavbarListItem path="/" title="Home" icon={<HomeIcon />} />
        <Divider />
        {data.role === 'Admin' ? (
          <>
            <ListSubheader inset>Setting</ListSubheader>
            <NavbarListItem
              path="/categories"
              title="Categories"
              icon={<AssignmentIcon />}
            />
            <NavbarListItem path="/users" title="Users" icon={<PersonIcon />} />
          </>
        ) : (
          ''
        )}
      </List>
    </>
  );

  return (
    <>
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          drawerOpen && matches && role === 'Admin' && classes.appBarShift
        )}
      >
        <Toolbar className={classes.toolbar}>
          <Hidden smDown={role !== 'Admin'} smUp={role !== 'Admin'}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={() => handleMeunIconClick(matches)}
              className={clsx(
                classes.menuButton,
                drawerOpen && matches && classes.menuButtonHidden
              )}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          </Hidden>
          <Typography className={classes.title}>
            <img src={logo} alt="logo" className={classes.logo} />
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
          <Typography className={classes.name}>
            {`Login as ${data.netId}`}
          </Typography>
        </Toolbar>
      </AppBar>
      <Hidden smDown={role !== 'Admin'} smUp={role !== 'Admin'}>
        <nav
          className={clsx(
            drawerOpen ? classes.drawerOpen : classes.drawerClose
          )}
          aria-label="nav"
        >
          <Hidden smUp>
            <Drawer
              container={container}
              variant="temporary"
              anchor="left"
              open={mobileOpen}
              onClose={handleMobileDrawerToggle}
              classes={{
                paper: classes.drawerPaper,
              }}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
            >
              {drawer}
            </Drawer>
          </Hidden>
          <Hidden xsDown>
            <Drawer
              variant="permanent"
              classes={{
                paper: clsx(
                  classes.drawerPaper,
                  !drawerOpen && classes.drawerPaperClose
                ),
              }}
              open={drawerOpen}
            >
              {drawer}
            </Drawer>
          </Hidden>
        </nav>
      </Hidden>
      <div className={classes.offset} />
    </>
  );
};

Navbar.propTypes = {
  // eslint-disable-next-line react/require-default-props
  window: PropTypes.func,
};

export default Navbar;
